import { Link } from 'react-router-dom';
import { useDashboard } from '../hooks/useDashboard';
import IdeaCard from '../components/ideas/IdeaCard';
import { ClockIcon, CheckCircleIcon, LightBulbIcon } from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const { data, loading } = useDashboard();

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <div className="skeleton h-8 w-40 mb-6" />
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="skeleton h-9 w-16 mx-auto mb-2" />
              <div className="skeleton h-4 w-20 mx-auto" />
            </div>
          ))}
        </div>
        <div className="skeleton h-6 w-48 mb-3" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="skeleton h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Failed to load dashboard</div>;

  const { stats, stale_ideas, nearly_ready, recent_activity } = data;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Dashboard</h1>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 border-t-4 border-t-gray-300 dark:border-t-gray-600 text-center">
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.draft}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Drafts</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 border-t-4 border-t-blue-400 text-center">
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.developing}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Developing</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 border-t-4 border-t-green-400 text-center">
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.ready}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Ready</p>
        </div>
      </div>

      {stale_ideas.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <ClockIcon className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Needs Attention</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">These ideas haven't been touched in over a week</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {stale_ideas.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        </div>
      )}

      {nearly_ready.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nearly Ready</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">These ideas are almost ready to publish</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {nearly_ready.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
          <Link to="/ideas" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
            View all
          </Link>
        </div>
        {recent_activity.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 rounded-full bg-indigo-50 dark:bg-indigo-950 flex items-center justify-center mb-4">
              <LightBulbIcon className="w-8 h-8 text-indigo-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 mb-2">No ideas yet. Start developing!</p>
            <Link
              to="/capture"
              className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              Develop your first idea
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {recent_activity.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
